import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, AlertTriangle, Search, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_REASONS = ["Paint Defect", "Damage", "Assembly Error", "Incorrect Dimensions", "Missing Components", "Machining Error", "Other"];
const FALLBACK_PART_NAMES = ["Adjustable Shelf", "Back Cleat", "Base end panel", "Bottom", "Bulkhead Filler Cut 60mm", "Cleat", "Deck", "Door Slab", "Drawer Box Back", "Drawer Box Bottom", "Drawer Box Partition", "Drawer Stretcher", "Filler Screw Together", "Finished Back", "Finished Left End", "Finished Right End", "Fixed Shelf", "Floor Base Panel", "Front Toe", "Front Top Stretcher", "Laundry Cleat", "LED Strip Screw Together", "Panel", "Panel Screw together", "Partition", "Scriber", "Side Cleat", "Side Cleat LH", "Side Cleat RH", "Sink Back Packer", "Top", "Top Shelf and Rail", "Under Panel", "Unfinished Back", "Unfinished Left End", "Unfinished Right End", "Upper Panel"];
const OTHER = "__other__";
const SEARCH_CHECKLIST = ["Checked rework rack", "Checked finished parts rack", "Checked machine offcuts / other jobs", "Checked with optimiser it was cut"];

export default function NewRequest() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [job, setJob] = useState(params.get("job") || "");
  const [part, setPart] = useState(params.get("part") || "");
  const [partNames, setPartNames] = useState(FALLBACK_PART_NAMES);
  const [partName, setPartName] = useState("");
  const [customPart, setCustomPart] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reasons, setReasons] = useState(FALLBACK_REASONS);
  const [form, setForm] = useState({ description: "", rework_reason: "Paint Defect", comments: "", request_type: "Rework", priority: "Normal", required_by: "", search_checklist: [] });
  const navigate = useNavigate();
  const isMissing = form.request_type === "Missing";
  const checklistDone = SEARCH_CHECKLIST.every((c) => form.search_checklist.includes(c));
  const toggleCheck = (c) => setForm((f) => ({ ...f, search_checklist: f.search_checklist.includes(c) ? f.search_checklist.filter((x) => x !== c) : [...f.search_checklist, c] }));
  const resolvedPartName = (partName === OTHER ? customPart : partName).trim();

  useEffect(() => {
    api.get("/reasons").then(({ data }) => {
      if (Array.isArray(data.reasons) && data.reasons.length) {
        setReasons(data.reasons);
        setForm((f) => (data.reasons.includes(f.rework_reason) ? f : { ...f, rework_reason: data.reasons[0] }));
      }
    }).catch(() => {});
    api.get("/part-names").then(({ data }) => {
      if (Array.isArray(data.part_names) && data.part_names.length) setPartNames(data.part_names);
    }).catch(() => {});
  }, []);

  const runCheck = async (e) => {
    e?.preventDefault();
    if (!job.trim() || !part.trim()) {
      toast.error("Enter both Job Number and Part Number");
      return;
    }
    if (!resolvedPartName) {
      toast.error("Select a Part name");
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.get("/reworks/check-duplicate", {
        params: { job_number: job.trim().toUpperCase(), part_number: part.trim().toUpperCase(), part_name: resolvedPartName },
      });
      setCheckResult(data);
      if (!data.exists) {
        toast.success("No duplicate found — you can proceed");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Check failed");
    } finally {
      setChecking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isMissing && !checklistDone) {
      toast.error("Complete the search checklist before logging a missing part");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/reworks", {
        job_number: job.trim().toUpperCase(),
        part_number: part.trim().toUpperCase(),
        part_name: resolvedPartName,
        description: form.description.trim(),
        rework_reason: form.rework_reason,
        comments: form.comments.trim(),
        request_type: form.request_type,
        priority: form.priority,
        required_by: form.required_by || null,
        search_checklist: isMissing ? form.search_checklist : [],
      });
      toast.success("Rework request submitted");
      navigate(`/rework/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.rework) {
        setCheckResult({ exists: true, rework: detail.rework });
        setStep(1);
        toast.error("Duplicate detected");
      } else {
        toast.error(formatApiError(detail) || "Submit failed");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="New Rework Request"
        subtitle="Two steps: check for an existing active rework, then submit the request."
        testid="new-header"
      />

      <div className="p-6 md:p-10 max-w-3xl">
        {/* Step indicator */}
        <div className="flex items-center gap-3 mb-8">
          {[1, 2].map((n) => (
            <div key={n} className="flex items-center gap-3 flex-1">
              <div className={`w-8 h-8 rounded-sm border flex items-center justify-center num text-sm ${
                step >= n ? "bg-[#424e43] text-[#f7f5f1] border-[#424e43]" : "bg-white border-[#cfcfc9] text-[#a39f98]"
              }`}>{n}</div>
              <div className={`text-xs uppercase tracking-widest ${step >= n ? "text-[#232826]" : "text-[#a39f98]"}`}>
                {n === 1 ? "Duplicate check" : "Submission"}
              </div>
              {n === 1 && <div className={`flex-1 h-px ${step >= 2 ? "bg-[#424e43]" : "bg-[#e6e5df]"}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={runCheck} className="space-y-6" data-testid="dup-check-form">
            <div className="border border-[#e6e5df] bg-white p-6 rounded-sm space-y-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono">Step 1 — Duplicate check</div>
              <p className="text-sm text-[#6b6a68]">Check for any active rework on this Job + Part combination before submitting.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Job Number</Label>
                  <Input value={job} onChange={(e) => setJob(e.target.value)} placeholder="e.g. JOB1042" required
                    data-testid="dup-job" className="bg-white border-[#cfcfc9] rounded-sm mt-2 uppercase text-[#232826]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Part Number</Label>
                  <Input value={part} onChange={(e) => setPart(e.target.value)} placeholder="e.g. ABC-100" required
                    data-testid="dup-part" className="bg-white border-[#cfcfc9] rounded-sm mt-2 uppercase text-[#232826]" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Part name</Label>
                  <Select value={partName} onValueChange={(v) => { setPartName(v); if (v !== OTHER) setCustomPart(""); }}>
                    <SelectTrigger className="bg-white border-[#cfcfc9] rounded-sm mt-2 text-[#232826]" data-testid="dup-part-name">
                      <SelectValue placeholder="Select a part name" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#e6e5df] max-h-72">
                      {partNames.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      <SelectItem value={OTHER}>Other (to fill manually)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {partName === OTHER && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Part name (manual)</Label>
                    <Input value={customPart} onChange={(e) => setCustomPart(e.target.value)} placeholder="Type the part name" autoFocus
                      data-testid="dup-part-name-custom" className="bg-white border-[#cfcfc9] rounded-sm mt-2 text-[#232826]" />
                  </div>
                )}
              </div>

              <Button type="submit" disabled={checking} data-testid="dup-check-btn"
                className="bg-[#e6e5df] hover:bg-[#cfcfc9] text-[#232826] border border-[#cfcfc9] rounded-sm uppercase text-xs tracking-widest">
                <Search size={14} className="mr-2" /> {checking ? "Checking…" : "Check register"}
              </Button>
            </div>

            {checkResult?.exists && (
              <div className="border border-[#8a3a2a]/40 bg-[#8a3a2a]/8 p-6 rounded-sm space-y-4" data-testid="dup-exists">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-[#8a3a2a] shrink-0 mt-0.5" size={20} />
                  <div>
                    <div className="font-display font-extrabold text-lg text-[#8a3a2a]">Active rework already exists</div>
                    <p className="text-sm text-[#424e43] mt-1">
                      An active rework request already exists for <span className="mono text-[#8a3a2a]">{checkResult.rework.job_number}</span> + <span className="mono text-[#8a3a2a]">{checkResult.rework.part_number}</span>. Please check the register before submitting another.
                    </p>
                  </div>
                </div>
                <div className="bg-white border border-[#e6e5df] p-4 rounded-sm flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#6b6a68] font-mono uppercase tracking-widest">{checkResult.rework.rework_reason}</div>
                    <div className="text-sm text-[#232826] mt-1">{checkResult.rework.description}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <StatusBadge status={checkResult.rework.status} />
                    <Link to={`/rework/${checkResult.rework.id}`} className="text-xs text-[#647265] hover:text-[#424e43] hover:underline" data-testid="dup-view-existing">View existing →</Link>
                  </div>
                </div>
              </div>
            )}

            {checkResult && !checkResult.exists && (
              <div className="border border-[#647265]/40 bg-[#647265]/8 p-6 rounded-sm space-y-4" data-testid="dup-clear">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#424e43] shrink-0 mt-0.5" size={20} />
                  <div>
                    <div className="font-display font-extrabold text-lg text-[#424e43]">No duplicates — you are clear</div>
                    <p className="text-sm text-[#424e43] mt-1">
                      No active rework exists for <span className="mono">{job.toUpperCase()}</span> + <span className="mono">{part.toUpperCase()}</span>. Proceed to submission.
                    </p>
                  </div>
                </div>
                <Button onClick={() => setStep(2)} data-testid="proceed-submit"
                  className="bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] rounded-sm uppercase text-xs tracking-widest">
                  Continue to submission →
                </Button>
              </div>
            )}
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submit} className="space-y-6" data-testid="submit-form">
            <div className="border border-[#e6e5df] bg-white p-6 rounded-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono">Step 2 — Submission</div>
                  <div className="mono text-xl mt-2 text-[#232826]">
                    {job.toUpperCase()} <span className="text-[#a39f98]">/</span> {part.toUpperCase()}
                  </div>
                  {resolvedPartName && <div className="text-sm text-[#647265] mt-1">{resolvedPartName}</div>}
                </div>
                <Button type="button" variant="ghost" onClick={() => setStep(1)} data-testid="back-to-check"
                  className="text-[#6b6a68] hover:text-[#424e43] hover:bg-[#efece5] rounded-sm">
                  <ArrowLeft size={14} className="mr-1.5" /> Back
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Request type</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {["Rework", "Missing"].map((t) => (
                      <button type="button" key={t} onClick={() => setForm({ ...form, request_type: t, rework_reason: t === "Missing" ? "Missing Components" : form.rework_reason })}
                        data-testid={`type-${t.toLowerCase()}`}
                        className={`border rounded-sm p-3 text-left transition-colors ${form.request_type === t ? (t === "Missing" ? "border-rose-400 bg-rose-50" : "border-[#424e43] bg-[#efece5]") : "border-[#cfcfc9] bg-white hover:bg-[#efece5]"}`}>
                        <div className="font-semibold text-sm text-[#232826]">{t === "Missing" ? "Missing part" : "Rework"}</div>
                        <div className="text-xs text-[#6b6a68] mt-0.5">{t === "Missing" ? "Part can't be found — search first" : "Part exists but needs re-doing"}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {isMissing && (
                  <div className="border border-rose-200 bg-rose-50/60 rounded-sm p-4 space-y-2" data-testid="missing-checklist">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-rose-800 font-mono">Find it first — confirm each</div>
                    {SEARCH_CHECKLIST.map((c, i) => (
                      <label key={c} className="flex items-center gap-3 text-sm text-[#232826] cursor-pointer">
                        <Checkbox checked={form.search_checklist.includes(c)} onCheckedChange={() => toggleCheck(c)} data-testid={`checklist-${i}`} className="border-rose-400 data-[state=checked]:bg-rose-600 data-[state=checked]:border-rose-600" />
                        {c}
                      </label>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="bg-white border-[#cfcfc9] rounded-sm mt-2 text-[#232826]" data-testid="submit-priority"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-[#e6e5df]">
                        {["Low", "Normal", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Required by (optional)</Label>
                    <Input type="date" value={form.required_by} onChange={(e) => setForm({ ...form, required_by: e.target.value })} data-testid="submit-required-by"
                      className="bg-white border-[#cfcfc9] rounded-sm mt-2 text-[#232826]" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Rework reason</Label>
                  <Select value={form.rework_reason} onValueChange={(v) => setForm({ ...form, rework_reason: v })}>
                    <SelectTrigger className="bg-white border-[#cfcfc9] rounded-sm mt-2 text-[#232826]" data-testid="submit-reason">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#e6e5df]">
                      {reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Description</Label>
                  <Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Describe the defect / rework required"
                    data-testid="submit-description" className="bg-white border-[#cfcfc9] rounded-sm mt-2 resize-none text-[#232826]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Additional comments (optional)</Label>
                  <Textarea rows={2} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })}
                    data-testid="submit-comments" className="bg-white border-[#cfcfc9] rounded-sm mt-2 resize-none text-[#232826]" />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={saving || (isMissing && !checklistDone)} data-testid="submit-btn"
                  className="bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] rounded-sm uppercase text-xs tracking-widest">
                  {saving ? "Submitting…" : isMissing ? "Log missing part" : "Submit rework request"}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
